import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Path, StackSimple, Circle, Planet, GlobeHemisphereWest, ShootingStar } from "@phosphor-icons/react";
import TopNav from "@/components/TopNav";
import SolarSystem from "@/components/SolarSystem";
import GalaxyMap from "@/components/GalaxyMap";
import ObjectPanel from "@/components/ObjectPanel";
import SearchCommand from "@/components/SearchCommand";
import DeepDive from "@/components/DeepDive";
import SkyEvents from "@/components/SkyEvents";
import api from "@/lib/api";

export default function Explore() {
  const [objects, setObjects] = useState([]);
  const [scale, setScale] = useState("solar"); // solar | galaxy
  const [selectedId, setSelectedId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [deepDiveObj, setDeepDiveObj] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showOrbits, setShowOrbits] = useState(true);
  const [paused, setPaused] = useState(false);
  const [showConstellations, setShowConstellations] = useState(false);
  const [closeUp, setCloseUp] = useState(false);
  const [skyOpen, setSkyOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { api.get("/objects").then((r) => setObjects(r.data.objects)); }, []);

  const realmOf = useCallback((id) => {
    if (id?.startsWith("star-")) return "galaxy";
    const o = objects.find((x) => x.id === id);
    return o?.realm || "solar";
  }, [objects]);

  const selectObject = useCallback((id) => {
    const realm = id?.startsWith("star-") ? "galaxy" : (objects.find((x) => x.id === id)?.realm || "solar");
    setScale(realm);
    setCloseUp(false);
    setSelectedId(id);
    setFocusToken((t) => t + 1);
  }, [objects]);

  const focusClose = useCallback((obj) => {
    setCloseUp(true);
    setPaused(true);
    setSelectedId(obj.id);
    setFocusToken((t) => t + 1);
  }, []);

  useEffect(() => {
    const obj = searchParams.get("object");
    if (obj) {
      selectObject(obj);
      searchParams.delete("object");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, selectObject, setSearchParams]);

  const openDeepDive = useCallback(async (idOrObj) => {
    const id = typeof idOrObj === "string" ? idOrObj : idOrObj.id;
    if (typeof idOrObj === "object") { setDeepDiveObj(idOrObj); return; }
    const r = await api.get(`/objects/${id}`);
    setDeepDiveObj(r.data.object);
  }, []);

  const switchScale = (s) => {
    setScale(s);
    setSelectedId(null);
    setCloseUp(false);
  };

  return (
    <div className="fixed inset-0 vignette" data-testid="explore-page">
      <div className="absolute inset-0 z-0">
        {objects.length > 0 && scale === "solar" && (
          <SolarSystem objects={objects} selectedId={selectedId} focusToken={focusToken}
            onSelect={selectObject} onDeepDive={openDeepDive} showLabels={showLabels} showOrbits={showOrbits} paused={paused} closeUp={closeUp} />
        )}
        {scale === "galaxy" && (
          <GalaxyMap selectedId={selectedId} focusToken={focusToken} onSelect={selectObject}
            onDeepDive={openDeepDive} onReturnSolar={() => switchScale("solar")} showConstellations={showConstellations} />
        )}
      </div>

      <TopNav onSearch={() => setSearchOpen(true)} />

      {/* Scale switcher */}
      <div data-testid="scale-switcher" className="fixed top-20 left-1/2 -translate-x-1/2 z-20 glass rounded-full p-1 flex gap-1">
        <ScalePill active={scale === "solar"} onClick={() => switchScale("solar")} icon={Planet} label="Solar System" testid="scale-solar" />
        <ScalePill active={scale === "galaxy"} onClick={() => switchScale("galaxy")} icon={GlobeHemisphereWest} label="Milky Way" testid="scale-galaxy" />
      </div>

      {/* Layer controls */}
      {scale === "solar" && (
        <div data-testid="layer-controls" className="fixed left-5 bottom-6 z-20 glass rounded-2xl p-2 flex flex-col gap-1">
          <LayerToggle testid="toggle-labels" active={showLabels} onClick={() => setShowLabels((v) => !v)} icon={StackSimple} label="Labels" />
          <LayerToggle testid="toggle-orbits" active={showOrbits} onClick={() => setShowOrbits((v) => !v)} icon={Circle} label="Orbits" />
          <LayerToggle testid="toggle-motion" active={!paused} onClick={() => setPaused((v) => !v)} icon={Path} label="Motion" />
        </div>
      )}
      {scale === "galaxy" && (
        <div data-testid="galaxy-layer-controls" className="fixed left-5 bottom-6 z-20 glass rounded-2xl p-2 flex flex-col gap-1">
          <LayerToggle testid="toggle-constellations" active={showConstellations} onClick={() => setShowConstellations((v) => !v)} icon={Path} label="Lines" />
        </div>
      )}

      {!selectedId && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-20 text-center pointer-events-none">
          <p className="text-tel text-[10px] text-white/40">
            {scale === "solar" ? "Click to select · double-click for analysis · drag to orbit" : "Click a star · double-click for full analysis · drag to explore the galaxy"}
          </p>
        </div>
      )}

      {selectedId && (
        <ObjectPanel objectId={selectedId} onClose={() => setSelectedId(null)} onDeepDive={openDeepDive} onViewClose={focusClose} />
      )}

      {/* Sky Tonight */}
      <button data-testid="sky-events-toggle" onClick={() => setSkyOpen(true)}
        className="fixed right-5 bottom-6 z-20 glass rounded-full px-4 py-3 flex items-center gap-2 text-sm text-white/80 hover:text-cyan-200 transition-colors">
        <ShootingStar size={18} weight="duotone" color="#00D8FF" /> Sky Tonight
      </button>
      <SkyEvents open={skyOpen} onClose={() => setSkyOpen(false)} />

      {deepDiveObj && <DeepDive object={deepDiveObj} onClose={() => setDeepDiveObj(null)} />}
      <SearchCommand open={searchOpen} onClose={() => setSearchOpen(false)} onSelect={selectObject} />
    </div>
  );
}

function ScalePill({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${active ? "bg-cyan-400 text-[#030305] font-semibold" : "text-white/60 hover:text-white"}`}>
      <Icon size={16} weight="duotone" /> {label}
    </button>
  );
}

function LayerToggle({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active ? "text-cyan-300 bg-cyan-400/10" : "text-white/45 hover:text-white/80"}`}>
      <Icon size={18} weight="duotone" />
      <span className="text-xs w-12 text-left">{label}</span>
    </button>
  );
}
